<template>
  <div ref="container" class="plv-ia-app plv-ia-checkin">
    <check-in-start
      v-show="curType === TYPE.checkInStart"
      :initial-tips="checkInTips || defaultCheckInTips"
      :initial-duration="checkInDuration"
      :right="right"
      @start="beforeHandlePublishClick"
      @show-history="showHistory"
    />
    <check-in-history
      v-show="curType === TYPE.checkInHistory"
      ref="history"
      :main-data="recordList"
      :right="right"
      @close="changeType(TYPE.checkInStart)"
      @show-history="showHistory"
      @show-detail="showDetail"
      @download-history="downloadCheckInHistory"
    />
    <check-in-ongoing
      v-show="curType === TYPE.checkInOngoing"
      :remaining-time="remainingTime"
      :check-in-num="checkInNum"
      :check-in-tips="checkInTips || defaultCheckInTips"
      @stop="endCheckin"
    />
    <check-in-detail
      v-show="curType === TYPE.checkInDetail"
      :main-data="recordDetail"
      :cur-check-in-id="curCheckInId"
      :right="right"
      @download-detail="downloadCheckInDetail"
      @close="handleDetailClose"
      @get-more="getMoreRecordDetail"
      @send-result="handleSendResult"
    />

    <!-- 研讨会选择人群弹窗 -->
    <send-room-modal
      v-if="showSendRoomModal"
      :send-room-title="sendRoomTitle"
      :lang="lang"
      :send-room-list="sendRoomList"
      :confirm-text="sendRoomConfirmText"
      @change-send-room-list="$emit('change-send-room-list', $event)"
      @confirm="handleSendRoomModalConfirm"
      @close="showSendRoomModal = false"
    />
  </div>
</template>

<script>
import Locale from '@/mixins/locale';
import CheckInStart from './CheckInStart';
import CheckInHistory from './CheckInHistory';
import CheckInOngoing from './CheckInOngoing';
import CheckInDetail from './CheckInDetail';
import SendRoomModal from '../../components/vclass/send-room-modal';
import commonMixin from '../app-mixin';
import sendRoomMixin from '../../mixins/vclass/send-room';

const TYPE = {
  checkInStart: 'checkInStart',
  checkInOngoing: 'checkInOngoing',
  checkInHistory: 'checkInHistory', // 记录列表页
  checkInDetail: 'checkInDetail', // 记录详情页
};

export default {
  name: 'CheckIn',

  components: {
    CheckInHistory,
    CheckInOngoing,
    CheckInDetail,
    CheckInStart,
    SendRoomModal
  },

  mixins: [commonMixin, Locale, sendRoomMixin],

  props: {
    /** 签到 SDK 实例 */
    core: {
      type: Object,
      default: null,
    },

    /** 需要显示结果的签到 id */
    resultId: {
      type: String,
      default: '',
    },

    /** 操作权限 */
    right: {
      type: Object,
      default() {
        return {
          /** 发送结果的操作权限 */
          sendResult: false,
          /** 是否显示“历史签到记录”入口 */
          showHistoryEntry: true,
          /** 是否显示下载链接 */
          download: true,
        };
      },
    },

    customCheckInTips: {
      type: [String, null],
      default: null,
    }
  },

  data() {
    return {
      TYPE,
      curType: this.resultId ? TYPE.checkInDetail : TYPE.checkInStart,

      // 签到提示语
      checkInTips: '',
      // 预设的签到时长
      checkInDuration: 30,

      recordList: [],
      // 签到记录相关数据
      recordDetail: {},

      // 正在签到
      isCheckingIn: false,
      // 剩余签到时间
      remainingTime: 0,
      // 倒计时timer
      timer: null,
      // 当前签到id
      curCheckInId: '',
      // 当前签到人数
      checkInNum: 0,
      // 判断是否重复发送
      isOverStart: false,
      // 发送签到参数
      startCheckInParams: {},
      socketConnected: true
    };
  },

  computed: {
    defaultCheckInTips() {
      return this.customCheckInTips || this.t('plvIA.checkIn.startTips');
    }
  },

  watch: {
    resultId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.showResult(val);
        }
      }
    }
  },

  beforeDestroy() {
    this.unbindCoreListeners();
    this.clearLimitTimer();
  },

  methods: {
    afterCoreInited() {
      this.bindCoreListeners();
      this.getCurrentStatus();
    },

    bindCoreListeners() {
      this.core.bindSocketIOEvents();
      this.core
        .on('start', this.handleSocketStart)
        .on('stop', this.handleSocketStop)
        .on('show-result', this.handleShowResultMsg)
        .on(this.core.socketIOEvents.DISCONNECT, () => {
          // console.info('Socket disconnect');
          this.socketConnected = false;
        })
        .on(this.core.socketIOEvents.CONNECT, () => {
          this.socketConnected = true;
          this.isOverStart = false;
        });
    },

    unbindCoreListeners() {
      this.core
        .off('start', this.handleSocketStart)
        .off('stop', this.handleSocketStop)
        .off('show-result', this.handleShowResultMsg);
    },

    handleShowResultMsg({ checkInId }) {
      this.$emit('show-result', { checkInId });
      this.showResult(checkInId);
    },

    // socket 消息：开始签到
    handleSocketStart({ checkInId }) {
      this.playSoundEffects();
      this.curCheckInId = checkInId;
      this.getCurrentStatus();
    },

    // socket 消息：停止签到
    handleSocketStop() {
      if (!this.isCheckingIn) return;
      this.isCheckingIn = false;
      this.clearLimitTimer();
      this.showDetail();
    },

    // 清理组件内签到状态
    clearCheckInStatus() {
      this.isCheckingIn = false;
      this.clearLimitTimer();
    },

    // 获取当前签到状态
    async getCurrentStatus() {
      if (this.isCheckingIn) return;
      const { isCheckingIn, ...checkInStatus } = await this.core.getCheckInStatus();
      this.isOverStart = false;
      if (!isCheckingIn) return;
      this.inSigingStatus({
        duration: checkInStatus.duration,
        tips: checkInStatus.tips,
        checkInId: checkInStatus.checkInId,
        remainingTime: checkInStatus.remainingTime,
      });
      return { isCheckingIn };
    },

    changeType(type) {
      this.curType = type;
    },

    inSigingStatus({ checkInId, tips, duration, remainingTime }) {
      if (this.isCheckingIn) return;
      this.isCheckingIn = true;
      this.curCheckInId = checkInId;
      this.checkInTips = tips;
      this.checkInDuration = duration;

      // 轮询签到时间
      this.getTime(remainingTime);
      this.changeType(TYPE.checkInOngoing);
    },

    // 研讨会 判断是否选择人群发送
    beforeHandlePublishClick(params) {
      // 断网提示
      if (!this.socketConnected) {
        this.$iaMessage.default(this.t('plvIA.common.networkErr'));
        return;
      }
      this.startCheckInParams = params;
      if (this.useSeminarChatApi && this.sendRoomList.length > 0) {
        const sendTarget = this.sendRoomList.filter(item => !!item.disabled === false);
        if (sendTarget.length > 1) {
          this.showSendRoomModal = true;
        } else {
          // 判断只有一个人群可选择，默认选中直接发送
          sendTarget[0].checked = true;
          params.sendTarget = sendTarget;
          this.startCheckIn(params);
        }
      } else {
        this.startCheckIn(params);
      }
    },

    handleSendRoomModalConfirm() {
      this.showSendRoomModal = false;
      this.startCheckInParams.sendTarget = this.sendRoomList;
      this.startCheckIn(this.startCheckInParams);
    },

    // 开始签到
    async startCheckIn({ tips, duration, sendTarget }) {
      if (!this.canStart) {
        this.$iaMessage.default(this.cantStartTips);
        return;
      }
      if (this.isCheckingIn) return;

      this.checkInTips = tips;
      this.checkInDuration = duration;
      if (this.isOverStart) return;

      sendTarget = this.getSendTargetParam(sendTarget);
      try {
        this.isOverStart = true;
        await this.core.start({ tips, duration, sendTarget });
      } catch (error) {
        this.isOverStart = false;
        this.$iaMessage.default(error.message);
      }
    },

    // 结束签到
    async endCheckin() {
      if (!this.isCheckingIn) return;
      this.clearLimitTimer();

      await this.core.stop();
    },

    clearLimitTimer() {
      clearInterval(this.timer);
      this.timer = null;
    },

    getTime(value) {
      this.clearLimitTimer();
      this.remainingTime = value;
      this.timer = setInterval(async () => {
        if (value <= 0) {
          this.clearLimitTimer();
          this.showDetail();
        } else {
          value -= 1;
          this.remainingTime = value;
          this.checkInNum = await this.core.getCheckInNum();
        }
      }, 1000);
    },

    getDateRange(data) {
      return Object.assign({
        startDate: this.$refs.history.dateValue[0],
        endDate: this.$refs.history.dateValue[1],
      }, data);
    },

    async showHistory(data) {
      data = this.getDateRange(data);
      this.recordList = await this.core.getCheckInHistory(data);
      this.changeType(TYPE.checkInHistory);
    },

    /**
     * 下载历史列表文件。
     */
    downloadCheckInHistory(data) {
      data = this.getDateRange(data);
      this.core.downloadCheckInHistory(data);
    },

    async showDetail(checkInId = '') {
      await this.getMoreRecordDetail({ checkInId });
      this.changeType(TYPE.checkInDetail);
    },

    async getMoreRecordDetail({ page = 1, checkInId } = {}) {
      const pageSize = 30;
      checkInId = checkInId || this.curCheckInId;

      const recordData = await this.core.getCheckInDetail({
        checkInId,
        page,
        pageSize,
      });

      const isEnd = !recordData.list || recordData.list.length < pageSize;
      if (page === 1) { // 获取首屏数据
        this.recordDetail = {
          list: recordData.list,
          onlineNum: recordData.onlineNum,
          checkInNum: recordData.checkInNum,
          checkInTime: recordData.checkInTime,
          checkInId,
          page: 1,
          isEnd,
        };
      } else { // 下拉加载更多
        this.$set(this.recordDetail, 'list', [...this.recordDetail.list, ...recordData.list]);
        this.$set(this.recordDetail, 'page', page);
        this.$set(this.recordDetail, 'isEnd', isEnd);
      }
    },

    downloadCheckInDetail() {
      this.core.downloadCheckInDetail({
        checkInId: this.recordDetail.checkInId,
        checkInTime: this.recordDetail.checkInTime,
      });
    },

    // 关闭记录详情页
    handleDetailClose() {
      const isFromHistoryList = this.recordDetail.checkInId && this.curCheckInId !== this.recordDetail.checkInId;
      if (isFromHistoryList) {
        if (this.recordList.length > 0) {
          this.changeType(TYPE.checkInHistory);
        } else {
          this.showHistory();
        }
      } else {
        this.changeType(TYPE.checkInStart);
      }
    },

    // 显示签到结果。
    showResult(checkInId) {
      this.showDetail(checkInId);
    },

    async handleSendResult(checkInId) {
      try {
        await this.core.sendResult(checkInId);
        this.$iaMessage.default(this.t('plvIA.common.sentSuccessfullyTips'));
      } catch (error) {
        this.$iaMessage.default(error.message || this.t('plvIA.common.failedToSentTips'));
      }
    },
  }
};
</script>

<style lang="scss">
@import '../../assets/styles/main.scss';

.plv-ia-checkin {
  height: 100%;
}
</style>
